import { useState, useEffect, createContext, useContext, lazy, Suspense, Component } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { Download, RefreshCw, X } from "lucide-react";
import { API } from "./lib/api";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ChatbotAttentionBubble } from "./components/ChatbotAttentionBubble";

// Error Boundary — prevents full app crashes when a component fails
const CHUNK_ERROR_RE = /ChunkLoadError|Loading chunk|Loading CSS chunk|Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i;
const isChunkLoadError = (error) =>
  !!error && (error.name === "ChunkLoadError" || CHUNK_ERROR_RE.test(String(error.message || error)));

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, updating: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error, updating: isChunkLoadError(error) };
  }

  componentDidCatch(error, errorInfo) {
    console.error("ErrorBoundary caught:", error, errorInfo);
    // A new version was deployed while this tab still had the old bundle: fetch the new one once, automatically.
    if (isChunkLoadError(error)) {
      const key = "chunk-reload-at";
      const last = Number(sessionStorage.getItem(key) || 0);
      if (Date.now() - last > 30000) {
        sessionStorage.setItem(key, String(Date.now()));
        if ("caches" in window) {
          caches.keys().then((names) => Promise.all(names.map((n) => caches.delete(n)))).finally(() => window.location.reload());
        } else {
          window.location.reload();
        }
      }
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.state.updating) {
        return (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", minHeight: "100dvh", background: "#0A0A0A", color: "#fff", gap: "16px", padding: "24px", textAlign: "center" }} data-testid="app-updating-screen">
            <div className="spinner"></div>
            <h2 style={{ fontSize: "18px", fontWeight: 600 }}>Updating to the latest version…</h2>
            <p style={{ color: "#A1A1AA", fontSize: "14px", maxWidth: "400px" }}>The app was just updated. Reloading automatically.</p>
            <button
              onClick={() => window.location.reload()}
              style={{ padding: "10px 24px", background: "#0099FF", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "14px", fontWeight: 500 }}
              data-testid="app-updating-reload-btn"
            >
              Reload now
            </button>
          </div>
        );
      }
      return (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", minHeight: "100dvh", background: "#0A0A0A", color: "#fff", gap: "16px", padding: "24px", textAlign: "center" }}>
          <div style={{ fontSize: "48px" }}>!</div>
          <h2 style={{ fontSize: "20px", fontWeight: 600 }}>Something went wrong</h2>
          <p style={{ color: "#A1A1AA", fontSize: "14px", maxWidth: "400px" }}>
            An unexpected error occurred. Please refresh the page to continue.
          </p>
          <button
            onClick={() => { this.setState({ hasError: false, error: null }); window.location.reload(); }}
            style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 24px", background: "#0099FF", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "14px", fontWeight: 500 }}
          >
            Refresh Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Lazy-loaded heavy pages (improves initial bundle size & navigation speed)
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const WorkerDashboard = lazy(() => import("./pages/WorkerDashboard"));
const ClientDashboard = lazy(() => import("./pages/ClientDashboard"));
const BusinessDashboard = lazy(() => import("./pages/BusinessDashboard"));
const TradeDashboard = lazy(() => import("./pages/TradeDashboard"));
const AccountantDashboard = lazy(() => import("./pages/AccountantDashboard"));
const PaymentReportsPage = lazy(() => import("./pages/PaymentReportsPage"));
const LoyaltyPortal = lazy(() => import("./pages/LoyaltyPortal"));
const QuotationsPage = lazy(() => import("./pages/client/QuotationsPage"));
const GanttView = lazy(() => import("./components/admin/management/GanttView"));
const BookingPage = lazy(() => import("./pages/BookingPage"));
const SignDocumentPage = lazy(() => import("./pages/SignDocumentPage"));
const UserProfilePage = lazy(() => import("./pages/UserProfilePage"));
const LoyaltyPage = lazy(() => import("./pages/LoyaltyPage"));

// Eagerly loaded (small / needed immediately)
import OfflineBanner from "./components/OfflineBanner";

// HomePage is lazy-loaded so the public landing page ships a lean main bundle
// (the LCP hero image is preloaded in index.html so first paint stays fast).
const HomePage = lazy(() => import("./pages/HomePage"));

// Lazy-loaded auth & static pages
const LoginPage = lazy(() => import("./pages/LoginPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsOfServicePage = lazy(() => import("./pages/TermsOfServicePage"));
const PaymentLinkPage = lazy(() => import("./pages/PaymentLinkPage"));
const TrueLayerRedirectPage = lazy(() => import("./pages/TrueLayerRedirectPage"));
const PublicQuotationPage = lazy(() => import("./pages/PublicQuotationPage"));
const PublicEstimatePage = lazy(() => import("./pages/PublicEstimatePage"));
const PublicInvoicePage = lazy(() => import("./pages/PublicInvoicePage"));
const CleaningPricingPage = lazy(() => import("./pages/CleaningPricingPage"));
const PricingListPage = lazy(() => import("./pages/PricingListPage"));
const AndroidAppPrompt = lazy(() => import("./components/AndroidAppPrompt"));
const FloatingCalculator = lazy(() => import("./components/shared/FloatingCalculator"));

// Lazy-loaded secondary pages
const ReferralProgram = lazy(() => import("./components/client/ReferralProgram"));

// Section-level Error Boundary for dashboard content (doesn't crash the whole app)
class SectionErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Section error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "300px", gap: "12px" }}>
          <p style={{ color: "#A1A1AA", fontSize: "14px" }}>This section encountered an error.</p>
          <button
            onClick={() => this.setState({ hasError: false })}
            style={{ padding: "8px 16px", background: "#262626", color: "#fff", border: "1px solid #333", borderRadius: "6px", cursor: "pointer", fontSize: "13px" }}
          >
            Try Again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Minimal loading fallback
const PageLoader = () => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", minHeight: "100dvh", background: "#0A0A0A" }}>
    <div style={{ width: "32px", height: "32px", border: "3px solid #0099FF", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
  </div>
);

// Redirect component for /admin/* to /dashboard/*
const AdminRedirect = () => {
  const location = useLocation();
  const newPath = location.pathname.replace('/admin', '/dashboard') + location.search;
  return <Navigate to={newPath} replace />;
};

// Components
const ChatbotWidget = lazy(() => import("./components/ChatbotWidget"));
import LanguageSelector, { LanguageProvider, useLanguage } from "./components/LanguageSelector";
const ForceRefreshButton = lazy(() => import("./components/ForceRefreshButton"));
import BackButtonHandler from "./components/BackButtonHandler";
const NotificationPermissionPromptLazy = lazy(() => import("./components/NotificationPermissionPrompt").then(m => ({ default: m.NotificationPermissionPrompt })));
import SEOHead from "./components/SEOHead";
import ProfileCompletionGate from "./components/ProfileCompletionGate";
import GoogleAuthCallback from "./components/auth/GoogleAuthCallback";
import { ModalProvider } from "./context/ModalContext";
import { ThemeProvider, useTheme } from "./components/ThemeProvider";
import { ConfirmActionProvider } from "./hooks/useConfirmAction";

// Use current origin for API calls to avoid stale deployment URLs
// (BACKEND_URL/API and global axios defaults live in ./lib/api)
export { API };
export { useLanguage, useTheme };

// PWA Install Context
const PWAContext = createContext(null);

export const usePWA = () => {
  const context = useContext(PWAContext);
  return context || { canInstall: false, promptInstall: () => {}, isInstalled: false };
};

// PWA Provider - manages install prompt state globally
export const PWAProvider = ({ children }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const promptInstall = async () => {
    if (!deferredPrompt) return false;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setDeferredPrompt(null);
      return true;
    }
    return false;
  };

  return (
    <PWAContext.Provider value={{ 
      canInstall: !!deferredPrompt && !isInstalled, 
      promptInstall,
      isInstalled 
    }}>
      {children}
    </PWAContext.Provider>
  );
};

// PWA Install Prompt Component - iOS ONLY (Android uses native browser prompt automatically)
const PWAInstallPrompt = () => {
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
  // Only show on public/homepage paths, not inside authenticated dashboards
  const isPublicPage = ['/', '/login', '/register', '/book'].some(p => location.pathname === p || location.pathname.startsWith('/book'));

  useEffect(() => {
    // Don't show if already installed as PWA
    if (isStandalone) return;
    // ONLY show for iOS devices - Android handles PWA install natively
    if (!isIOS) return;
    if (!isPublicPage) return;
    
    // Reset dismiss after 7 days
    const dismissedTime = localStorage.getItem('pwa_dismissed');
    if (dismissedTime && Date.now() - parseInt(dismissedTime) > 7 * 24 * 60 * 60 * 1000) {
      localStorage.removeItem('pwa_dismissed');
    }
    if (localStorage.getItem('pwa_dismissed')) return;

    // Show iOS guide after 1.5s
    const timer = setTimeout(() => setVisible(true), 1500);
    return () => clearTimeout(timer);
  }, [isIOS, isPublicPage, isStandalone]);

  const handleDismiss = () => {
    setVisible(false);
    setDismissed(true);
    localStorage.setItem('pwa_dismissed', Date.now().toString());
  };

  const handleLater = () => {
    setVisible(false);
    // Don't persist - show again next session
  };

  // Only render for iOS devices that haven't dismissed and are on public pages
  if (!visible || dismissed || !isIOS || isStandalone) return null;

  // iOS Safari prompt — guide to Share → Add to Home Screen
  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[9999]"
      style={{ animation: 'slideUpFade 0.4s ease-out' }}
      data-testid="pwa-ios-prompt"
    >
      {/* Arrow pointing to Safari share bar */}
      <div className="flex justify-center mb-1">
        <svg width="28" height="16" viewBox="0 0 28 16" fill="none">
          <path d="M14 16 L2 0 L26 0 Z" fill="#1C1C1E" />
        </svg>
      </div>

      <div className="bg-[#1C1C1E] border-t border-[#3A3A3C] rounded-t-2xl shadow-2xl px-5 pt-4 pb-6">
        {/* Handle bar */}
        <div className="w-10 h-1 bg-[#48484A] rounded-full mx-auto mb-4" />

        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-2xl overflow-hidden flex-shrink-0 shadow-md border border-white/10">
            <img src="/logo192.png" alt="X-TREME" className="w-full h-full object-cover"
              onError={(e) => { e.target.style.display='none'; }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white font-semibold text-base leading-tight">X-TREME CONCEPT</p>
            <p className="text-[#8E8E93] text-sm leading-tight mt-0.5">Add to Home Screen</p>
          </div>
          <button
            onClick={handleDismiss}
            className="w-8 h-8 rounded-full bg-[#3A3A3C] flex items-center justify-center flex-shrink-0"
            aria-label="Close"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M1 1l10 10M11 1L1 11" stroke="#8E8E93" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* Step-by-step instructions */}
        <div className="space-y-3 mb-5">
          <div className="flex items-center gap-3 p-3 bg-[#2C2C2E] rounded-xl">
            <div className="w-8 h-8 rounded-full bg-[#0099FF] flex items-center justify-center flex-shrink-0 text-white font-bold text-sm">1</div>
            <div className="flex-1">
              <p className="text-white text-sm font-medium">Tap the Share button</p>
              <p className="text-[#8E8E93] text-xs">The <span className="text-[#0099FF]">↑</span> icon in Safari</p>
            </div>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#0099FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>
              <polyline points="16 6 12 2 8 6"/>
              <line x1="12" y1="2" x2="12" y2="15"/>
            </svg>
          </div>
          <div className="flex items-center gap-3 p-3 bg-[#2C2C2E] rounded-xl">
            <div className="w-8 h-8 rounded-full bg-[#0099FF] flex items-center justify-center flex-shrink-0 text-white font-bold text-sm">2</div>
            <div className="flex-1">
              <p className="text-white text-sm font-medium">Tap "Add to Home Screen"</p>
              <p className="text-[#8E8E93] text-xs">Scroll down in the Share menu</p>
            </div>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#34C759" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="2" width="20" height="20" rx="4"/>
              <line x1="12" y1="8" x2="12" y2="16"/>
              <line x1="8" y1="12" x2="16" y2="12"/>
            </svg>
          </div>
        </div>

        <button
          onClick={handleLater}
          className="w-full py-3 bg-[#2C2C2E] text-[#8E8E93] rounded-xl text-sm font-medium"
          data-testid="pwa-ios-later-btn"
        >
          Maybe later
        </button>
      </div>
    </div>
  );
};

// Add animation keyframes globally
const pwaStyles = `
  @keyframes slideUpFade {
    from { opacity: 0; transform: translateY(100%); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;
if (typeof document !== 'undefined' && !document.getElementById('pwa-styles')) {
  const style = document.createElement('style');
  style.id = 'pwa-styles';
  style.textContent = pwaStyles;
  document.head.appendChild(style);
}

// Auth Context — extracted to ./context/AuthContext (re-exported here for
// the many components that import { useAuth } from "../App").
export { useAuth, AuthProvider };

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, isAuthenticated } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Dashboard Router
const DashboardRouter = () => {
  const { user } = useAuth();
  const location = useLocation();

  // Handle specific routes within dashboard
  if (location.pathname === "/dashboard/referral" && user?.role === "client") {
    return <ReferralProgram />;
  }

  if (user?.role === "god_admin" || user?.role === "admin") {
    return <AdminDashboard />;
  } else if (user?.role === "lead_worker") {
    return <AdminDashboard />;
  } else if (user?.role === "worker") {
    return <WorkerDashboard />;
  } else if (user?.role === "trade") {
    return <TradeDashboard />;
  } else if (user?.role === "accountant") {
    return <AccountantDashboard />;
  } else if (user?.client_type === "business") {
    return <BusinessDashboard />;
  } else {
    return <ClientDashboard />;
  }
};

function AppContent() {
  const { loading } = useAuth();
  const location = useLocation();
  const [showSlowMessage, setShowSlowMessage] = useState(false);

  useEffect(() => {
    if (loading) {
      const timer = setTimeout(() => setShowSlowMessage(true), 2000);
      return () => clearTimeout(timer);
    }
    setShowSlowMessage(false);
  }, [loading]);

  // Google OAuth return: exchange the session_id BEFORE any protected route runs
  if (location.hash?.includes("session_id=")) {
    return <GoogleAuthCallback />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex flex-col items-center justify-center gap-4">
        <div className="spinner"></div>
        {showSlowMessage && (
          <p className="text-gray-400 text-sm animate-pulse">
            Connecting to server...
          </p>
        )}
      </div>
    );
  }

  return (
    <>
      <SEOHead />
      <OfflineBanner />
      <Suspense fallback={null}><ForceRefreshButton /></Suspense>
      <BackButtonHandler />
      <Suspense fallback={null}><AndroidAppPrompt /></Suspense>
      <Suspense fallback={null}><FloatingCalculator /></Suspense>
      <div className="noise-overlay"></div>
      <Suspense fallback={<PageLoader />}>
      <SectionErrorBoundary>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms-of-service" element={<TermsOfServicePage />} />
        <Route path="/pay/:token" element={<Suspense fallback={null}><PaymentLinkPage /></Suspense>} />
        <Route path="/sign/:token" element={<Suspense fallback={null}><SignDocumentPage /></Suspense>} />
        <Route path="/truelayer/redirect" element={<ProtectedRoute><Suspense fallback={null}><TrueLayerRedirectPage /></Suspense></ProtectedRoute>} />
        <Route path="/q/:quotationId" element={<Suspense fallback={null}><PublicQuotationPage /></Suspense>} />
        <Route path="/e/:estimateId" element={<Suspense fallback={null}><PublicEstimatePage /></Suspense>} />
        <Route path="/r/:reportId" element={<Suspense fallback={null}><PublicEstimatePage /></Suspense>} />
        <Route path="/i/:invoiceId" element={<Suspense fallback={null}><PublicInvoicePage /></Suspense>} />
        <Route path="/cleaning-prices" element={<CleaningPricingPage />} />
        <Route path="/pricing-list" element={<PricingListPage />} />
        <Route
          path="/book"
          element={<ProtectedRoute><BookingPage /></ProtectedRoute>}
        />
        <Route
          path="/loyalty"
          element={<ProtectedRoute><LoyaltyPortal /></ProtectedRoute>}
        />
        <Route
          path="/loyalty/legacy"
          element={<ProtectedRoute><LoyaltyPage /></ProtectedRoute>}
        />
        <Route
          path="/payment-reports"
          element={<ProtectedRoute><PaymentReportsPage /></ProtectedRoute>}
        />
        <Route
          path="/quotations"
          element={<ProtectedRoute><QuotationsPage /></ProtectedRoute>}
        />
        <Route
          path="/quotations/:quotationId"
          element={<ProtectedRoute><QuotationsPage /></ProtectedRoute>}
        />
        <Route
          path="/profile"
          element={<ProtectedRoute><UserProfilePage /></ProtectedRoute>}
        />
        <Route
          path="/dashboard/quotations/:quotationId"
          element={<ProtectedRoute><QuotationsPage /></ProtectedRoute>}
        />
        <Route
          path="/dashboard/timeline"
          element={<ProtectedRoute allowedRoles={['god_admin', 'admin']}><GanttView /></ProtectedRoute>}
        />
        <Route
          path="/dashboard/*"
          element={
            <ProtectedRoute>
              <ProfileCompletionGate>
                <DashboardRouter />
              </ProfileCompletionGate>
            </ProtectedRoute>
          }
        />
        {/* Redirect /admin/* to /dashboard/* for backward compatibility */}
        <Route path="/admin/*" element={<ProtectedRoute><AdminRedirect /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </SectionErrorBoundary>
      </Suspense>
      <Suspense fallback={null}><ChatbotWidget /></Suspense>
      <PWAInstallPrompt />
      <Suspense fallback={null}><NotificationPermissionPromptLazy /></Suspense>
      <Toaster position="top-right" theme="dark" richColors />
      
      <ChatbotAttentionBubble />
      {/* Permanent bottom cover to overlay Emergent badge (hidden while a mobile bottom nav is mounted) */}
      <div
        className="emergent-badge-cover fixed bottom-0 left-0 right-0 h-8 z-[2147483646] pointer-events-none"
        style={{ background: 'linear-gradient(to top, #0A0A0A 60%, transparent)' }}
      />
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <PWAProvider>
              <LanguageProvider googleApiKey={process.env.REACT_APP_GOOGLE_TRANSLATE_KEY}>
                <ModalProvider>
                  <ConfirmActionProvider>
                    <AppContent />
                  </ConfirmActionProvider>
                </ModalProvider>
              </LanguageProvider>
            </PWAProvider>
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
